// Geometry of direct manipulation in the editor: handles, snapping and the focal-point pan.
// Pure functions in artboard pixels; Editor.tsx only turns pointer events into calls here.
import type { ImageLayer, Layer, Layout } from '../../lib/contentTypes';

export type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
export const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
export const SNAP_PX = 6; // screen pixels

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
/** A guide line to draw while dragging: a vertical line at x, or a horizontal one at y. */
export interface Guide {
  axis: 'x' | 'y';
  pos: number;
}
export interface Targets {
  x: number[];
  y: number[];
}

/** Lines a dragged layer snaps to: artboard edges and centre, the safe margins, and the edges and
 *  centres of the other visible layers of the artboard. */
export function snapTargets(layout: Layout, ab: number, excludeId: string): Targets {
  const W = layout.width;
  const H = layout.height;
  const m = (layout.safe_margin_pct / 100) * W;
  const x = [0, W / 2, W, m, W - m];
  const y = [0, H / 2, H, m, H - m];
  for (const l of layout.artboards[ab].layers) {
    if (l.id === excludeId || l.hidden) continue;
    x.push(l.x, l.x + l.w / 2, l.x + l.w);
    y.push(l.y, l.y + l.h / 2, l.y + l.h);
  }
  return { x, y };
}

/** The offset that brings the nearest of `values` onto the nearest target, if within `tol`. */
function nearest(values: number[], targets: number[], tol: number): { delta: number; at: number } | null {
  let best: { delta: number; at: number } | null = null;
  for (const v of values) {
    for (const t of targets) {
      const d = t - v;
      if (Math.abs(d) <= tol && (!best || Math.abs(d) < Math.abs(best.delta))) best = { delta: d, at: t };
    }
  }
  return best;
}

/** Moves `r` to (x, y) and snaps its left/centre/right and top/middle/bottom to the targets. */
export function snapMove(r: Rect, t: Targets, tol: number): { x: number; y: number; snappedX: boolean; snappedY: boolean; guides: Guide[] } {
  const sx = nearest([r.x, r.x + r.w / 2, r.x + r.w], t.x, tol);
  const sy = nearest([r.y, r.y + r.h / 2, r.y + r.h], t.y, tol);
  const guides: Guide[] = [];
  if (sx) guides.push({ axis: 'x', pos: sx.at });
  if (sy) guides.push({ axis: 'y', pos: sy.at });
  return { x: r.x + (sx?.delta ?? 0), y: r.y + (sy?.delta ?? 0), snappedX: !!sx, snappedY: !!sy, guides };
}

const MIN_W = 4;
const MIN_H = 2;

/** The rectangle after dragging `handle` by (dx, dy) from `start`. `keepRatio` (a corner with Shift)
 *  keeps the start aspect ratio; `targets` (with `tol`) snaps the moving edges. */
export function resizeRect(start: Rect, handle: Handle, dx: number, dy: number, opts: { keepRatio: boolean; targets: Targets | null; tol: number }): { rect: Rect; guides: Guide[] } {
  const guides: Guide[] = [];
  const west = handle.includes('w');
  const east = handle.includes('e');
  const north = handle.includes('n');
  const south = handle.includes('s');
  const ratioLock = opts.keepRatio && handle.length === 2;
  let left = start.x + (west ? dx : 0);
  let right = start.x + start.w + (east ? dx : 0);
  let top = start.y + (north ? dy : 0);
  let bottom = start.y + start.h + (south ? dy : 0);
  const snapEdge = (axis: 'x' | 'y', value: number) => {
    if (!opts.targets) return value;
    const s = nearest([value], axis === 'x' ? opts.targets.x : opts.targets.y, opts.tol);
    if (!s) return value;
    guides.push({ axis, pos: s.at });
    return s.at;
  };
  if (west) left = snapEdge('x', left);
  if (east) right = snapEdge('x', right);
  if (!ratioLock) {
    if (north) top = snapEdge('y', top);
    if (south) bottom = snapEdge('y', bottom);
  }
  if (right - left < MIN_W) {
    if (west) left = right - MIN_W;
    else right = left + MIN_W;
  }
  if (ratioLock) {
    // the width decides; the height follows from the start ratio
    const ratio = start.w / start.h;
    const h = Math.max(MIN_H, (right - left) / ratio);
    if (north) top = bottom - h;
    else bottom = top + h;
  } else if (bottom - top < MIN_H) {
    if (north) top = bottom - MIN_H;
    else bottom = top + MIN_H;
  }
  return { rect: { x: left, y: top, w: right - left, h: bottom - top }, guides };
}

/** The focal point after the picture inside an image box was dragged by (dx, dy) artboard pixels.
 *  Only a `fill` image pans; the result stays inside 0..1. `natural` is the drawn file's size. */
export function panFocal(layer: ImageLayer, natural: { w: number; h: number }, dx: number, dy: number): { focal_x: number; focal_y: number } {
  const sw = layer.crop.w * natural.w;
  const sh = layer.crop.h * natural.h;
  const scale = Math.max(layer.w / sw, layer.h / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  // where the picture's left/top edge is now (same clamp as the renderer)
  const offX = Math.min(0, Math.max(layer.w - dw, layer.w / 2 - layer.focal_x * dw));
  const offY = Math.min(0, Math.max(layer.h - dh, layer.h / 2 - layer.focal_y * dh));
  const clamp = (v: number) => Math.min(1, Math.max(0, Math.round(v * 1000) / 1000));
  return {
    focal_x: dw > layer.w ? clamp((layer.w / 2 - (offX + dx)) / dw) : layer.focal_x,
    focal_y: dh > layer.h ? clamp((layer.h / 2 - (offY + dy)) / dh) : layer.focal_y,
  };
}

/** The parts of other slides' span layers that land on slide `ab`: ghost boxes to click. */
export function ghostsFor(layout: Layout, ab: number): { layer: Layer; owner: number; x: number }[] {
  const out: { layer: Layer; owner: number; x: number }[] = [];
  layout.artboards.forEach((other, j) => {
    if (j === ab) return;
    for (const l of other.layers) {
      if (!l.span || l.hidden) continue;
      const x = l.x + (j - ab) * layout.width;
      if (x >= layout.width || x + l.w <= 0) continue;
      out.push({ layer: l, owner: j, x });
    }
  });
  return out;
}
